import FilterListIcon from "@mui/icons-material/FilterList";
import {
	Box,
	Chip,
	Grid,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Menu,
	MenuList,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
	Assignment,
	AssignmentStatus,
	AssignmentStatusKey,
	statusKeysToValues,
	statusToThemeKey,
} from "../../api/assignments/assignment";
import { useStatusChoices } from "../../hooks/use-status-choices";

export const DEFAULT_PICKER_STATUS_KEYS: AssignmentStatusKey[] = [
	"ACTIVE",
	"PREPARATION",
	"SENT_TO_CLIENT",
];

interface UseAssignmentFilterResult {
	searchQuery: string;
	setSearchQuery: (q: string) => void;
	activeStatusKeys: string[];
	setActiveStatusKeys: (keys: string[]) => void;
	filteredAssignments: Assignment[];
}

export const useAssignmentFilter = (
	assignments: Assignment[],
	defaultStatusKeys: string[] = DEFAULT_PICKER_STATUS_KEYS
): UseAssignmentFilterResult => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeStatusKeys, setActiveStatusKeys] =
		useState<string[]>(defaultStatusKeys);

	const filteredAssignments = useMemo(() => {
		const activeStatusValues = statusKeysToValues(activeStatusKeys);
		const queryTokens = searchQuery
			.trim()
			.toLowerCase()
			.split(/\s+/)
			.filter(Boolean);
		return assignments
			.filter(
				(a) => a.status !== undefined && activeStatusValues.includes(a.status)
			)
			.filter((a) => {
				if (queryTokens.length === 0) return true;
				const haystack = [a.name, a.company_name, a.end_client_name]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return queryTokens.every((token) => haystack.includes(token));
			})
			.sort((a, b) => {
				const aActive = a.status === AssignmentStatus.ACTIVE ? 0 : 1;
				const bActive = b.status === AssignmentStatus.ACTIVE ? 0 : 1;
				if (aActive !== bActive) return aActive - bActive;
				return (a.company_name ?? "") > (b.company_name ?? "") ? 1 : -1;
			});
	}, [assignments, searchQuery, activeStatusKeys]);

	return {
		searchQuery,
		setSearchQuery,
		activeStatusKeys,
		setActiveStatusKeys,
		filteredAssignments,
	};
};

interface AssignmentFilterBarProps {
	searchQuery: string;
	onSearchChange: (q: string) => void;
	activeStatusKeys: string[];
	onStatusKeysChange: (keys: string[]) => void;
	searchPlaceholder?: string;
	autoFocus?: boolean;
}

export const AssignmentFilterBar = ({
	searchQuery,
	onSearchChange,
	activeStatusKeys,
	onStatusKeysChange,
	searchPlaceholder = "Search role, company or end client...",
	autoFocus = false,
}: AssignmentFilterBarProps) => {
	const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(
		null
	);
	const filterMenuOpen = Boolean(filterAnchorEl);
	const statusChoices = useStatusChoices();
	const theme = useTheme();

	const toggleStatus = (key: string) => {
		onStatusKeysChange(
			activeStatusKeys.includes(key)
				? activeStatusKeys.filter((k) => k !== key)
				: [...activeStatusKeys, key]
		);
	};

	return (
		<Stack
			direction="row"
			spacing={1}
			alignItems="center"
			sx={{ px: 2, py: 1 }}
			data-component="assignment-filter-bar"
		>
			<TextField
				data-component="assignment-filter-search"
				autoFocus={autoFocus}
				fullWidth
				size="small"
				type="search"
				placeholder={searchPlaceholder}
				value={searchQuery}
				onChange={(e) => onSearchChange(e.target.value)}
			/>
			<IconButton
				data-component="assignment-filter-button"
				onClick={(e) => setFilterAnchorEl(e.currentTarget)}
			>
				<FilterListIcon />
			</IconButton>
			<Menu
				data-component="assignment-filter-menu"
				open={filterMenuOpen}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box p={2} data-component="assignment-filter-content">
					<Typography data-component="assignment-filter-title">
						Filter Status
					</Typography>
					<MenuList
						sx={{ maxWidth: 250 }}
						data-component="assignment-filter-menu-list"
					>
						<Grid
							container
							spacing={1}
							data-component="assignment-filter-chips-grid"
						>
							{statusChoices.map((choice) => {
								const isActive = activeStatusKeys.includes(choice.key);
								const chipColor =
									theme.assignmentStatus[
										statusToThemeKey[choice.key]
									];
								return (
									<Grid
										data-component="assignment-filter-chip-grid"
										key={`assignment-filter-${choice.key}`}
									>
										<Chip
											data-component="assignment-filter-chip"
											label={choice.name}
											size="small"
											variant="outlined"
											sx={{
												backgroundColor: isActive
													? chipColor
													: null,
												color: isActive ? "common.black" : null,
												"&.MuiChip-root:hover": {
													backgroundColor: isActive
														? chipColor
														: null,
													color: isActive
														? "common.black"
														: null,
												},
											}}
											onClick={() => toggleStatus(choice.key)}
										/>
									</Grid>
								);
							})}
						</Grid>
					</MenuList>
				</Box>
			</Menu>
		</Stack>
	);
};

interface AssignmentPickerListProps {
	assignments: Assignment[];
	onPick: (assignmentId: number) => void;
	emptyMessage?: string;
}

export const AssignmentPickerList = ({
	assignments,
	onPick,
	emptyMessage = "No roles match your search",
}: AssignmentPickerListProps) => {
	const theme = useTheme();

	if (assignments.length === 0) {
		return (
			<Typography sx={{ p: 2, textAlign: "center" }} color="text.secondary">
				{emptyMessage}
			</Typography>
		);
	}

	return (
		<List data-component="assignment-picker-list">
			{assignments.map((a) => (
				<ListItemButton
					key={a.id}
					onClick={() => onPick(a.id)}
					data-component="assignment-picker-item"
				>
					<ListItemText
						primary={a.name}
						slotProps={{ secondary: { component: "div" } }}
						secondary={
							a.company_name ? (
								<Stack
									direction="row"
									spacing={0.5}
									alignItems="center"
								>
									<span>@{a.company_name}</span>
								</Stack>
							) : null
						}
					/>
					<Chip
						size="small"
						label={a.status_display}
						sx={{
							backgroundColor:
								theme.assignmentStatus[
									statusToThemeKey[AssignmentStatus[a.status]]
								],
							color: "common.black",
							ml: 1,
						}}
					/>
				</ListItemButton>
			))}
		</List>
	);
};
